import React from "react";

interface RoleDescription {
  role: string;
  description: string;
}

interface DoctorData {
  isSuccess: boolean;
  doctorId: number;
 locationId: number;
  locationName: string;
  departmentId?: number;
  departmentName?: string;
  specializationId?: number;
  specializationName?: string;
  name: string;
  qualification: string;
  designation: string | null;
  areaOfInterest: string;
  awardsAchievements: string | null;
  researchPublications: string | null;
  previousExperiance: string;
  languages: string;
  gender: string;
  phone: string;
  emailId: string;
  experienceYears: number;
  doctorFees: number;
  status: string;
  namePrefixCode?: string; 
}

interface MarketingFormData {
  name: string;
  designation: string;
  experience: string;
  locationName: string;
  departmentId?: number | null;
  fee: string;
  namePrefixCode: string;
  languages: string;
  qualification: string;
  summary: string;
  benefits: string;
  awards: string[];
  publications: string[];
  areaOfInterest: RoleDescription[];
  previousExperiance: RoleDescription[];
   specializationId?: number | null;
}

interface CompareModalProps {
  section: string;
  originalData: DoctorData;
  marketingData: MarketingFormData;
  onClose: () => void;
}

const CompareModal: React.FC<CompareModalProps> = ({
  section,
  originalData,
  marketingData,
  onClose
}) => {
  // Helper function to safely parse JSON strings
  const safeJSONParse = <T,>(jsonString: string | null, defaultValue: T): T => {
    if (!jsonString) return defaultValue;
    try {
      const parsed = JSON.parse(jsonString);
      return parsed as T;
    } catch (e) {
      console.error("JSON parse error:", e, "Input:", jsonString);
      return defaultValue;
    }
  };

  // Parse and transform array helper
  const parseAndTransformArray = (jsonString: string | null): RoleDescription[] => {
    if (!jsonString) return [];
    try {
      const parsed = JSON.parse(jsonString);
      return parsed.map((item: any) => ({
        role: item.Role || item.role || "",
        description: item.Description || item.description || ""
      }));
    } catch (e) {
      console.error("JSON parse error:", e, "Input:", jsonString);
      return [];
    }
  };

  const getSectionData = () => {
    switch (section) {
      case "name":
        return {
          title: "Doctor Name",
          icon: "👤",
          original: originalData.name,
          marketing: marketingData.name
        };
case "location":
  return {
    title: "Location",
    icon: "📍",
    original: originalData.locationName || "-",
    marketing: marketingData.locationName
  };
      case "designation":
        return {
          title: "Designation",
          icon: "💼",
          original: originalData.designation || "-",
          marketing: marketingData.designation
        };

      case "experience":
        return {
          title: "Experience",
          icon: "⏱️",
          original: `${originalData.experienceYears} Years`,
          marketing: marketingData.experience
        };

      case "fee":
        return {
          title: "Consultation Fee",
          icon: "💰",
          original: `₹${originalData.doctorFees}`,
          marketing: marketingData.fee
        };
        
     case "namePrefixCode":
  return {
    title: "Title",
    icon: "👨‍⚕️",  // Or use any appropriate icon
    original: originalData.namePrefixCode || "-",  // ✅ Remove currency symbol
    marketing: marketingData.namePrefixCode
  };

      case "languages":
        return {
          title: "Languages",
          icon: "🌐",
          original: originalData.languages || "English, Hindi",
          marketing: marketingData.languages
        };

      case "qualification":
        return {
          title: "Qualification",
          icon: "🎓",
          original: originalData.qualification,
          marketing: marketingData.qualification
        };

      case "summary":
        return {
          title: "Professional Summary",
          icon: "📝",
          original: "",
          marketing: marketingData.summary
        };

      case "awards":
        return {
          title: "Awards & Achievements",
          icon: "🏆",
          original: safeJSONParse<string[]>(originalData.awardsAchievements, []),
          marketing: marketingData.awards
        };

      case "publications":
        return {
          title: "Research & Publications",
          icon: "📄",
          original: safeJSONParse<string[]>(originalData.researchPublications, []),
          marketing: marketingData.publications
        };

      case "areaOfInterest":
        return {
          title: "Area of Interest",
          icon: "🎯",
          original: parseAndTransformArray(originalData.areaOfInterest),
          marketing: marketingData.areaOfInterest
        };

      case "previousExperiance":
        return {
          title: "Previous Experience",
          icon: "🏥",
          original: parseAndTransformArray(originalData.previousExperiance),
          marketing: marketingData.previousExperiance
        };

      default:
        return null;
    }
  };

  const data = getSectionData();
  if (!data) return null;

  const isModified = JSON.stringify(data.original) !== JSON.stringify(data.marketing);

  const renderContent = (content: any, isOriginal: boolean) => {
    if (Array.isArray(content)) {
      if (content.length === 0) {
        return (
          <div className="bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            <svg className="w-10 h-10 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            <p className="text-gray-500 text-sm font-medium">No items available</p>
          </div>
        );
      }

      // String array (Awards, Publications)
      if (typeof content[0] === "string") {
        return (
          <div className="space-y-1">
            {content.map((item: string, idx: number) => (
              <div
                key={idx}
                className={`flex items-start gap-3 p-1 rounded-lg border-l-4 ${
                  isOriginal 
                    ? 'bg-teal-50 border-teal-500' 
                    : 'bg-blue-50 border-blue-500'
                } shadow-sm hover:shadow-md transition-shadow`}
              >
                <span className={`flex-shrink-0 mt-1 w-4 h-4 rounded-full flex items-center justify-center text-xs font-bold ${
                  isOriginal 
                    ? 'bg-teal-500 text-white' 
                    : 'bg-blue-500 text-white'
                }`}>
                  {idx + 1}
                </span>
                <p className="text-sm text-gray-800 flex-1 leading-relaxed">{item}</p>
              </div>
            ))}
          </div>
        );
      } 
      // Object array (Area of Interest, Previous Experience)
      else {
        return (
          <div className="space-y-1">
            {content.map((item: RoleDescription, idx: number) => (
              <div
                key={idx}
                className={`rounded-lg border-2 p-1 shadow-sm hover:shadow-md transition-all ${
                  isOriginal 
                    ? 'bg-gradient-to-br from-teal-50 to-emerald-50 border-teal-300' 
                    : 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-300'
                }`}
              >
                <div className="flex items-start gap-2">
                  <span className={`flex-shrink-0 mt-1 w-4 h-4 rounded-full flex items-center justify-center text-xs font-bold shadow-sm ${
                    isOriginal 
                      ? 'bg-teal-500 text-white' 
                      : 'bg-blue-500 text-white'
                  }`}>
                    {idx + 1}
                  </span>
                  <p className="font-semibold text-gray-900 flex-1">{item.role}</p>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed pl-8 bg-white/50 rounded">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        );
      }
    }

    // Empty content
    if (!content || content === "") {
      return (
        <div className="bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
          <svg className="w-10 h-10 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
          </svg>
          <p className="text-gray-500 text-sm font-medium">No content available</p>
        </div>
      );
    }

    // Simple text content
    return (
      <div className={`p-4 rounded-lg border-2 shadow-sm ${
        isOriginal 
          ? 'bg-teal-50 border-teal-200' 
          : 'bg-blue-50 border-blue-200'
      }`}>
        <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{content}</p>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
      <div className="bg-white rounded-2xl shadow-2xl w-[80%] max-h-[90vh] overflow-hidden flex flex-col animate-slideUp">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-[#184A84] via-[#2563EB] to-[#3B82F6] text-white px-6 py-3 flex items-center justify-between shadow-lg">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 backdrop-blur-sm p-2 rounded-lg">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-bold">Content Comparison</h2>
              <p className="text-white/80 text-xs">Original vs Marketing Version</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 rounded-full w-10 h-10 flex items-center justify-center transition-all hover:rotate-90"
            title="Close"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Section Title Banner */}
        <div className="px-6 py-2 bg-gradient-to-r from-gray-50 to-blue-50 border-b-2 border-blue-200">
          <div className="flex items-center gap-3">
            <span className="text-3xl">{data.icon}</span>
            <div>
              <h3 className="font-bold text-lg text-gray-900">{data.title}</h3>
              <p className="text-xs text-gray-600">Compare the differences between versions</p>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-7xl mx-auto">
            {/* Original Version */}
            <div className="bg-white rounded-xl shadow-lg border-2 border-teal-200 overflow-hidden">
              <div className="bg-gradient-to-r from-[#009A85] to-[#00C9A7] px-5 py-3 flex items-center gap-2 shadow-md">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h4 className="font-bold text-white text-sm uppercase tracking-wide">Original Version</h4>
                <span className="ml-auto bg-white/30 backdrop-blur-sm px-2 py-1 rounded-full text-xs font-semibold text-white">
                  Source
                </span>
              </div>
              <div className="p-2 max-h-[500px] overflow-y-auto custom-scrollbar">
                {renderContent(data.original, true)}
              </div>
            </div>

            {/* Marketing Version */}
            <div className="bg-white rounded-xl shadow-lg border-2 border-blue-200 overflow-hidden">
              <div className="bg-gradient-to-r from-[#184A84] to-[#2563EB] px-5 py-3 flex items-center gap-2 shadow-md">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                <h4 className="font-bold text-white text-sm uppercase tracking-wide">Marketing Version</h4>
                <span className="ml-auto bg-white/30 backdrop-blur-sm px-2 py-1 rounded-full text-xs font-semibold text-white">
                  Edited
                </span>
              </div>
              <div className="p-2 max-h-[500px] overflow-y-auto custom-scrollbar">
                {renderContent(data.marketing, false)}
              </div>
            </div>
          </div>

          {/* Modified Status Banner */}
          {isModified ? (
            <div className="mt-6 max-w-7xl mx-auto">
              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-400 rounded-xl p-2 flex items-center gap-4 shadow-md">
                <div className="flex-shrink-0 w-10 h-10 bg-yellow-400 rounded-full flex items-center justify-center animate-pulse">
                  <svg className="w-7 h-7 text-yellow-900" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-yellow-900 font-bold text-sm">Content Modified</p>
                  <p className="text-yellow-800 text-xs mt-1">The marketing version has been edited and differs from the original</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-4 max-w-7xl mx-auto">
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-400 rounded-xl p-2 flex items-center gap-4 shadow-md">
                <div className="flex-shrink-0 w-10 h-10 bg-green-400 rounded-full flex items-center justify-center">
                  <svg className="w-7 h-7 text-green-900" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-green-900 font-bold text-sm">No Changes Detected</p>
                  <p className="text-green-800 text-xs">Both versions contain identical content</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer with Actions */}
        <div className="px-6 py-2 bg-gradient-to-r from-gray-50 to-blue-50 border-t-2 border-blue-200 flex justify-between items-center shadow-lg">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-xs">Review changes carefully before submitting</span>
          </div>
          <button
            onClick={onClose}
            className="px-6 py-1 bg-gradient-to-r from-[#184A84] to-[#2563EB] text-white rounded-lg hover:from-[#0F3A6F] hover:to-[#1D4ED8] font-semibold shadow-md hover:shadow-lg transition-all flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Close Comparison
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompareModal;
